/**
 * LoginPage — React replacement for src/dashboard/static/login.html (D-16, D-17).
 *
 * Behavior:
 *   - If already authenticated, redirect to intended destination (state.from) or `/`.
 *   - Surface denial/error messages from callback-redirect query params
 *     (`?denied=1` = role rejection, `?error=<code>` = handshake failure).
 *   - "Log in with Google" button performs a full-page redirect to
 *     /salescoach/auth/login (Phase 3 auth router). Backend handles OAuth
 *     handshake via AuthDB (which federates to Google SSO), then redirects
 *     back to /login-success#token=...
 *
 * The static login.html stays as a safety net if the SPA bundle fails to load
 * (Plan 04-12 wires the SPA fallback so React Router wins when JS is healthy).
 */

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

export function LoginPage() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const denied = searchParams.get('denied') === '1';
  const errorCode = searchParams.get('error');
  const sanitizedError = errorCode
    ? errorCode.replace(/[^a-z_]/gi, '').slice(0, 32)
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="bg-card border rounded-lg shadow-sm p-8 max-w-md w-full text-center">
        <h1 className="text-2xl font-semibold mb-2">Mr. Edge Dashboard</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Admin access only. Sign in with your EXANTE account to continue.
        </p>
        <Button
          className="w-full"
          size="lg"
          onClick={() => {
            window.location.assign('/salescoach/auth/login');
          }}
        >
          Log in with Google
        </Button>

        {denied ? (
          <p
            className="mt-4 text-sm text-destructive"
            data-testid="login-denied-message"
          >
            Access denied. Your role does not allow dashboard access. Contact an
            admin.
          </p>
        ) : null}
        {sanitizedError ? (
          <p
            className="mt-4 text-sm text-destructive"
            data-testid="login-error-message"
            data-error-code={sanitizedError}
          >
            Couldn&apos;t sign you in. Please try again.
          </p>
        ) : null}

        <p className="mt-6 text-xs text-muted-foreground">
          Your session is short-lived (&le; 8h). You&apos;ll be redirected back
          automatically after signing in.
        </p>
      </div>
    </div>
  );
}
